import type { Route, Leg } from '@metro/routing'
import type { LineDisplay } from '@metro/route-card'
import type { ConvenientCarType } from './carTypes'
import { dedupeConsecutiveRideStops } from './dedupeConsecutiveStops'
import {
  lineSubtitle,
  towardsLabel,
  stationsCountLabel,
  RouteDetailsStrings,
  type RouteDetailsStringSet,
} from './strings'

/**
 * Pure RouteDetails section fabric.
 *
 * Ported from iOS `RouteDetailsViewStateFabric.swift` (the cross-platform
 * logic source) but emits the **Figma row order** resolved in QA spec
 * §11-Q1, not the V1 stream order:
 *
 *   Validation(tap-in) → Entry(walk-in) → <line block(s)> →
 *   Entry(walk-out) → Validation(tap-out) → Map
 *
 * where each line block is
 *   FirstStop → Direction → Expand? → MiddleStop* → LastStop
 * and consecutive blocks are separated by a Transfer row.
 *
 * Chips (RoutesCollectionRow) + ReportIssue live in the host overlay,
 * not here — same split as iOS (`RouteDetailsView` owns them, the fabric
 * owns the section enum). The expand-row appears only when the line has
 * more than `3 + 1(direction)` stop entries, matching the V1 predicate;
 * when collapsed the middle stops are cut off.
 *
 * The web `Route` carries `legs` (ride / transfer / walk) with bare
 * `stationId`s, not the iOS resolved `Route.sections`. Station names are
 * resolved through the injected {@link StationResolver} and line display
 * through `lineLookup`, keeping the fabric schema-agnostic (same contract
 * as `RouteVariantRow`). Walk legs are ignored (entry/exit walks are
 * synthesised at the ends per the Figma order, exactly as V1 appends a
 * single entry/exit pair).
 */

/** A geographic coordinate (schema `coordinate` shape). */
export interface MapCoordinate {
  readonly latitude: number
  readonly longitude: number
}

/**
 * Resolves a stationId to its display info. Returns null if unknown.
 *
 * `coordinate` (optional) surfaces the schema's `{ latitude, longitude }`
 * so the pure-walk map can draw a street-following route between origin
 * and destination (iOS canon `MapRowView.ViewState.originCoordinate`).
 * Resolvers that don't carry geometry (test stubs, name-only lookups)
 * omit it — the map degrades to the static placeholder.
 *
 * `lineId` (optional) is the line the stop belongs to, keyed like
 * `lineLookup`; it labels street-walk rows. Without it the row shows no
 * line label — the id itself is never parsed (v1 and v2 spell ids
 * differently).
 */
export type StationResolver = (
  stationId: string,
) => { localName: string; coordinate?: MapCoordinate; lineId?: string } | null

/**
 * Display name for a stop. Keep-with-fallback (cross-platform contract,
 * 2026-05-25): when the station can't be resolved the raw `stationId` is
 * shown rather than an empty string, so the hop is never silently lost.
 */
function stationName(resolveStation: StationResolver, stationId: string): string {
  return resolveStation(stationId)?.localName ?? stationId
}

/** Trailing-slot content of a FirstStop row (mirrors V1 `extra`). */
export type FirstStopExtra =
  | { readonly kind: 'possibleTransfer'; readonly cars: readonly ConvenientCarType[] }
  | { readonly kind: 'upsell' }
  | null

export interface ValidationSection {
  readonly kind: 'validation'
  readonly id: string
  readonly validationType: 'entry' | 'exit'
}

export interface EntrySection {
  readonly kind: 'entry'
  readonly id: string
  readonly entryType: 'entry' | 'exit'
  /** Walk duration label, e.g. "5 min". Empty when unknown. */
  readonly totalTime: string
}

export interface FirstStopSection {
  readonly kind: 'firstStop'
  readonly id: string
  readonly sectionIndex: number
  readonly title: string
  readonly subtitle: string | null
  /** Line-block total ride time, e.g. "17 min". Empty/hidden for single-stop. */
  readonly totalTime: string
  readonly arrivalTime: string
  readonly hexColor: string
  readonly isOutlined: boolean
  readonly isSingleStop: boolean
  /** Platform sign to follow ("Westbound", …), verbatim; null → no chip. */
  readonly platformBound: string | null
  /**
   * "Towards <next stop>" under the chip (V1 `_getDirection` → stops[1]);
   * null on a single-stop block. Rendered inside the first-stop row with
   * the historical direction-row test id.
   */
  readonly towards: string | null
  readonly extra: FirstStopExtra
}

export interface ExpandSection {
  readonly kind: 'expand'
  readonly id: string
  readonly sectionIndex: number
  readonly title: string
  readonly isExpanded: boolean
  readonly hexColor: string
  readonly isOutlined: boolean
}

export interface MiddleStopSection {
  readonly kind: 'middleStop'
  readonly id: string
  readonly sectionIndex: number
  readonly rowIndex: number
  readonly title: string
  readonly arrivalTime: string
  readonly hexColor: string
  readonly isOutlined: boolean
}

export interface LastStopSection {
  readonly kind: 'lastStop'
  readonly id: string
  readonly sectionIndex: number
  readonly title: string
  readonly arrivalTime: string
  readonly hexColor: string
  readonly isOutlined: boolean
}

export interface TransferSection {
  readonly kind: 'transfer'
  readonly id: string
  readonly sectionIndex: number
  readonly title: string
  readonly totalTime: string
}

/**
 * Street walk between two DIFFERENT physical stations (2026-07 wire
 * contract): leg type `walk` + EMPTY `transition_id` + both endpoint
 * ids + `duration_s`. Canonical case: Edgware Road Circle → Edgware
 * Road Bakerloo. Rendered as "Walk to <station> (<line>)" with the
 * payload duration — NEVER folded into "Make a transfer" (that stays
 * the in-station interchange row).
 */
export interface StreetWalkSection {
  readonly kind: 'streetWalk'
  readonly id: string
  readonly sectionIndex: number
  /** Walk target station display name. */
  readonly stationName: string
  /** Target station's line display name (e.g. "Bakerloo line"); null when unresolvable. */
  readonly lineLabel: string | null
  /** Walk duration label, e.g. "5 min" — the platform's shared minutes
   *  label for `duration_s` (assertion-level contract; formatted-label
   *  section state is this module's convention). Empty when unknown. */
  readonly totalTime: string
}

/**
 * Selected-station endpoint marker of a street walk (iOS canon
 * @c9d7ecc61): a NEUTRAL hollow ring + bold station name — no line
 * chip, no time. Origin sits before a leading walk, destination after
 * a trailing walk.
 */
export interface StreetEndpointSection {
  readonly kind: 'streetEndpoint'
  readonly id: string
  readonly sectionIndex: number
  readonly role: 'origin' | 'destination'
  readonly stationName: string
}

export interface MapSection {
  readonly kind: 'map'
  readonly id: string
  readonly stationName: string
  /**
   * Map endpoints (iOS canon `MapRowView.ViewState.originCoordinate` +
   * destination `coordinate`). A walk map carries both — a pure-walk route,
   * or one street walk (leading, mid-route or, on the destination map,
   * trailing) — and draws a street-following
   * walking route between them; any other map carries only the destination
   * — a live map pinned on it. A `(0,0)`
   * schema fallback or an unresolved endpoint degrades to `null` (the
   * static single-pin placeholder).
   */
  readonly originCoordinate?: MapCoordinate | null
  readonly destinationCoordinate?: MapCoordinate | null
}

export type RouteDetailsSection =
  | ValidationSection
  | EntrySection
  | FirstStopSection
  | ExpandSection
  | MiddleStopSection
  | LastStopSection
  | TransferSection
  | StreetWalkSection
  | StreetEndpointSection
  | MapSection

export interface BuildSectionsInput {
  readonly route: Route
  readonly lineLookup: ReadonlyMap<string, LineDisplay>
  readonly resolveStation: StationResolver
  readonly isPremium: boolean
  /**
   * Per-line-block expanded flag keyed by ride-leg index. `true` reveals
   * middle stops; `false` (the default for an unset index) collapses to
   * first/direction/expand + last. Matches the V1
   * `prepareLocalStorages`, which seeds every line index to `false`
   * (collapsed) — so a freshly-opened modal renders S1, not S2.
   */
  readonly expanded: Readonly<Record<number, boolean>>
  /** Test/host seam for the "now" clock used for arrival HH:mm. */
  readonly clock?: () => Date
  /**
   * Convenient-transfer cars per ride-leg index. A leg with a transfer
   * after it AND a non-empty entry here drives the FirstStop extra slot:
   * premium → car-strip, non-premium → upsell banner (QA spec §1.3).
   */
  readonly possibleTransfersByLeg?: Readonly<Record<number, readonly ConvenientCarType[]>>
  /**
   * Platform sign ("Westbound", …) per ride-leg index — see
   * `buildPlatformBoundsByLeg`. A leg without an entry shows no chip.
   */
  readonly platformBoundsByLeg?: Readonly<Record<number, string>>
  /** Ride-leg indices whose line renders outlined (DLR / Overground / Trams). */
  readonly outlinedLegs?: ReadonlySet<number>
  /**
   * Localised label set. Defaults to the English
   * {@link RouteDetailsStrings}; the host passes a catalog-backed set
   * built via `buildRouteDetailsStrings(t)`.
   */
  readonly strings?: RouteDetailsStringSet
}

// Arrival clock of the city the app is built for: `VITE_TIME_ZONE`, which every
// build sets — `.env` for the default (London) build, `.env.<city>` for another
// city (e.g. America/New_York). No fallback: a build without it would be on the
// browser's zone, not the city's.
const ARRIVAL_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: import.meta.env.VITE_TIME_ZONE,
  hour12: false,
})

/**
 * Unresolved-line fallback colour. Cross-platform parity contract
 * (2026-05-25): a leg whose line isn't resolvable is KEPT (never
 * dropped) and rendered grey `#888888` with the raw `line_id` as its
 * subtitle — same on iOS/Android/Web.
 */
const FALLBACK_HEX = '#888888'

/** Minutes label "N min" — matches the chip / V1 `Route.Minutes`. */
function minutesLabel(durationS: number): string {
  if (durationS <= 0) return ''
  return `${Math.round(durationS / 60)} min`
}

/**
 * Resolve a station's map coordinate for the pure-walk route map, or
 * `null` when unusable. Mirrors iOS `MapRowView._isFallbackCoordinate`:
 * the schema `(0,0)` fallback (and a missing / unresolved coordinate)
 * degrades the map to the static placeholder rather than pointing it at
 * the Gulf of Guinea.
 */
function walkCoordinate(resolveStation: StationResolver, stationId: string): MapCoordinate | null {
  const coordinate = resolveStation(stationId)?.coordinate
  if (coordinate === undefined) return null
  if (coordinate.latitude === 0 && coordinate.longitude === 0) return null
  return { latitude: coordinate.latitude, longitude: coordinate.longitude }
}

/**
 * The walking map of a street walk — the rider leaves the system and walks a
 * street the metro map does not draw, so the route is shown on a map right
 * under the walk row, whatever its length (owner 2026-10-19). An in-station
 * change never gets here. `null` when an end has no usable coordinate (the
 * route cannot be drawn).
 */
function walkMapSection(
  index: number,
  leg: Leg,
  resolveStation: StationResolver,
): MapSection | null {
  const from = leg.from_station_id ?? ''
  const to = leg.to_station_id ?? ''
  const originCoordinate = walkCoordinate(resolveStation, from)
  const destinationCoordinate = walkCoordinate(resolveStation, to)
  if (originCoordinate === null || destinationCoordinate === null) return null
  return {
    kind: 'map',
    id: `walk-map-${index}`,
    stationName: stationName(resolveStation, to),
    originCoordinate,
    destinationCoordinate,
  }
}

function rideLegs(route: Route): { leg: Leg; index: number }[] {
  return route.legs.map((leg, index) => ({ leg, index })).filter((e) => e.leg.type === 'ride')
}

/**
 * Street walk per the 2026-07 wire contract: pedestrian leg between two
 * DIFFERENT stations with NO schema transition backing — type `walk`,
 * EMPTY `transition_id`, both endpoint ids present. In-station hops stay
 * type `transfer` (or `walk` WITH a transition_id) and keep folding into
 * "Make a transfer".
 */
function isStreetWalk(leg: Leg): boolean {
  if (leg.type !== 'walk') return false
  if ((leg.transition_id ?? '').length > 0) return false
  const from = leg.from_station_id ?? ''
  const to = leg.to_station_id ?? ''
  return from.length > 0 && to.length > 0 && from !== to
}

/**
 * The interchange between the ride leg at `legIndex` and the next ride
 * leg, folding **all** intervening non-ride legs (transfer + walk) into
 * one perceived transfer (cross-platform contract, 2026-05-25; a
 * standalone walk-only route is S10/P2, out of P1 scope). Returns the
 * summed duration in seconds, or `null` when no ride follows (the leg is
 * the last ride block, so no transfer row).
 */
function foldedTransferAfter(route: Route, legIndex: number): number | null {
  let duration = 0
  let sawNonRide = false
  for (let i = legIndex + 1; i < route.legs.length; i++) {
    const leg = route.legs[i]
    if (leg.type === 'ride') {
      // A following ride exists → the accumulated non-ride legs form the
      // interchange (which may be zero-length if rides are adjacent).
      return sawNonRide ? duration : null
    }
    sawNonRide = true
    duration += leg.duration_s
  }
  // Trailing non-ride legs with no ride after them are entry/exit walks,
  // not an interchange — no transfer row.
  return null
}

export function buildRouteDetailsSections(input: BuildSectionsInput): RouteDetailsSection[] {
  const {
    // Branch-junction duplicate-stop collapse (fix b): drop a ride leg's
    // leading stop when it is the SAME physical station (naptan-first) as
    // the prior ride's terminus — e.g. Camden Town Northern CTN2→CTN0 on a
    // Tufnell Park → Bank route. Done at the presentation build so it covers
    // both online and offline route sources. No-op (same reference) when
    // nothing collapses.
    route: rawRoute,
    lineLookup,
    resolveStation,
    isPremium,
    expanded,
    clock = () => new Date(),
    possibleTransfersByLeg = {},
    platformBoundsByLeg = {},
    outlinedLegs,
    strings = RouteDetailsStrings,
  } = input

  const route = dedupeConsecutiveRideStops(rawRoute)

  const now = clock().getTime()
  const arrivalAt = (offsetS: number): string =>
    ARRIVAL_FORMATTER.format(new Date(now + offsetS * 1000))

  const sections: RouteDetailsSection[] = []

  const lineDisplayForStation = (stationId: string): LineDisplay | undefined => {
    const lineId = resolveStation(stationId)?.lineId
    return lineId === undefined ? undefined : lineLookup.get(lineId)
  }

  const streetWalkSection = (legIndex: number, leg: Leg): StreetWalkSection => {
    const toId = leg.to_station_id ?? ''
    return {
      kind: 'streetWalk',
      id: `street-walk-${legIndex}`,
      sectionIndex: legIndex,
      stationName: stationName(resolveStation, toId),
      lineLabel: lineDisplayForStation(toId)?.localName ?? null,
      totalTime: minutesLabel(leg.duration_s),
    }
  }

  // Origin / destination endpoint marker for a street walk — a neutral
  // ring + name row for the station the user actually SELECTED, so the
  // list starts (ends) where the user starts (ends), not at the sibling
  // station the trains use. No line chip, no time (iOS canon @c9d7ecc61).
  const streetEndpointSection = (
    role: 'origin' | 'destination',
    legIndex: number,
    stationId: string,
  ): StreetEndpointSection => ({
    kind: 'streetEndpoint',
    id: `street-${role}`,
    sectionIndex: legIndex,
    role,
    stationName: stationName(resolveStation, stationId),
  })

  const rides = rideLegs(route)

  // DIRECT WALK route (2026-07 cross-platform contract, iOS canon
  // `RouteDetailsViewStateFabric.isPureWalk`): street-walk leg(s) with
  // ZERO ride/transfer legs — the user walks the whole way (e.g. Green
  // Park → Baker Street). The tube framing — Tap in / Walk to the
  // platform / Walk to the exit / Tap out — makes no sense when the user
  // never enters the system, so it is dropped entirely. Rows become
  // [origin marker → street walk → map], and the map draws a
  // street-following walking route between the two endpoints (real
  // MapKit at runtime, deterministic straight line under the test seam).
  // An entirely EMPTY route is NOT a pure walk — it falls through to the
  // degenerate framing below. Android / Web drop the same four rows.
  const streetWalks = route.legs
    .map((leg, index) => ({ leg, index }))
    .filter((e) => isStreetWalk(e.leg))
  const hasRideOrTransfer = route.legs.some((leg) => leg.type === 'ride' || leg.type === 'transfer')
  const isPureWalk = streetWalks.length > 0 && !hasRideOrTransfer

  if (isPureWalk) {
    const firstWalk = streetWalks[0]
    const lastWalk = streetWalks[streetWalks.length - 1]
    const originId = firstWalk.leg.from_station_id ?? ''
    const destId = lastWalk.leg.to_station_id ?? ''
    // Origin marker (the station the user actually SELECTED) → the
    // street-walk row(s) → the walking-route map. No validation / entry
    // / exit rows (no system entry).
    sections.push(streetEndpointSection('origin', firstWalk.index, originId))
    for (const { leg, index } of streetWalks) {
      sections.push(streetWalkSection(index, leg))
    }
    sections.push({
      kind: 'map',
      id: 'map',
      stationName: stationName(resolveStation, destId),
      originCoordinate: walkCoordinate(resolveStation, originId),
      destinationCoordinate: walkCoordinate(resolveStation, destId),
    })
    return sections
  }

  // Leading / trailing street walks exist only relative to a ride —
  // a walking-only route (no rides) keeps the plain V1-shaped list
  // (standalone walk-only rendering is S10/P2, out of scope).
  const firstRideLegIndex = rides.length > 0 ? rides[0].index : 0
  const lastRideLegIndex = rides.length > 0 ? rides[rides.length - 1].index : route.legs.length
  const leadingStreetWalks =
    rides.length > 0
      ? route.legs
          .slice(0, firstRideLegIndex)
          .map((leg, index) => ({ leg, index }))
          .filter((e) => isStreetWalk(e.leg))
      : []
  const trailingStreetWalks =
    rides.length > 0
      ? route.legs
          .map((leg, index) => ({ leg, index }))
          .slice(lastRideLegIndex + 1)
          .filter((e) => isStreetWalk(e.leg))
      : []

  // 1) Top of the list. Plain route: Validation (tap-in) then Entry
  //    (walk-in) — Figma pairs them at the top, validation FIRST (QA
  //    spec §1.2 / §11-Q1). With a LEADING street walk the route departs
  //    a different station than the user selected, so the list opens at
  //    the SELECTED station, walks the street, and only then taps in:
  //    Marker(selected) → StreetWalk → Validation(tap-in) → Entry.
  if (leadingStreetWalks.length > 0) {
    const first = leadingStreetWalks[0]
    sections.push(streetEndpointSection('origin', first.index, first.leg.from_station_id ?? ''))
    for (const { leg, index } of leadingStreetWalks) {
      sections.push(streetWalkSection(index, leg))
      const map = walkMapSection(index, leg, resolveStation)
      if (map !== null) sections.push(map)
    }
  }
  sections.push({ kind: 'validation', id: 'validation-entry', validationType: 'entry' })
  sections.push({ kind: 'entry', id: 'entry', entryType: 'entry', totalTime: '' })

  // 2) Per ride leg: a line block, separated from the next by a transfer
  //    (in-station) or an exit → street walk → re-entry sequence.
  rides.forEach(({ leg, index: legIndex }, blockIndex) => {
    const stops = leg.stops ?? []
    const display = leg.line_id !== undefined ? lineLookup.get(leg.line_id) : undefined
    const hexColor = display?.hexColor ?? FALLBACK_HEX
    const isOutlined = outlinedLegs?.has(legIndex) ?? false
    // Keep-with-fallback (cross-platform contract): an unresolved line
    // still gets a subtitle so the hop is never silently dropped. Both
    // iOS + Android append " line" to the raw line_id in this path
    // (iOS `_fallbackLine.shortLocalName = lineID` → "%@ line" template;
    // Android `lineSubtitle(leg.lineID)` appends " line") — match that
    // exactly so the three-way string is identical. Resolved →
    // "<localName> line"; unresolved → "<line_id> line"; missing line_id
    // → null.
    const subtitle =
      display !== undefined
        ? lineSubtitle(display.localName)
        : leg.line_id !== undefined
          ? lineSubtitle(`${leg.line_id}${strings.lineSuffix}`)
          : null

    const extra = resolveExtra({
      route,
      legIndex,
      isPremium,
      cars: possibleTransfersByLeg[legIndex],
    })

    if (stops.length <= 1) {
      const only = stops[0]
      sections.push({
        kind: 'firstStop',
        id: `first-stop-${legIndex}`,
        sectionIndex: legIndex,
        title: only !== undefined ? stationName(resolveStation, only.stationId) : '',
        subtitle,
        totalTime: '',
        arrivalTime: only !== undefined ? arrivalAt(only.arrival_offset_s) : '',
        hexColor,
        isOutlined,
        isSingleStop: true,
        platformBound: null,
        towards: null,
        extra,
      })
    } else {
      pushLineBlock({
        sections,
        stops,
        legIndex,
        hexColor,
        isOutlined,
        subtitle,
        totalTime: minutesLabel(leg.duration_s),
        platformBound: platformBoundsByLeg[legIndex] ?? null,
        extra,
        isExpanded: expanded[legIndex] ?? false,
        resolveStation,
        arrivalAt,
        strings,
      })
    }

    // Interchange between this block and the next.
    if (blockIndex < rides.length - 1) {
      const nextRideLegIndex = rides[blockIndex + 1].index
      const group = route.legs
        .map((l, index) => ({ leg: l, index }))
        .slice(legIndex + 1, nextRideLegIndex)
      const streetWalks = group.filter((e) => isStreetWalk(e.leg))
      if (streetWalks.length === 0) {
        // In-station interchange — one "Make a transfer" row per
        // interchange, folding all intervening transfer+walk legs
        // (unchanged pre-street-walk behaviour).
        const transferDuration = foldedTransferAfter(route, legIndex)
        if (transferDuration !== null) {
          sections.push({
            kind: 'transfer',
            id: `transfer-${legIndex}`,
            sectionIndex: legIndex,
            title: strings.makeATransfer,
            totalTime: minutesLabel(transferDuration),
          })
        }
      } else {
        // Street walk between stations mid-route: leave through the
        // gates, walk the street, re-enter — Entry(exit) → Tap out →
        // StreetWalk → Tap in → Entry(entry). Any in-station legs in
        // the same group still fold into a transfer row first.
        const nonStreetDuration = group
          .filter((e) => !isStreetWalk(e.leg))
          .reduce((sum, e) => sum + e.leg.duration_s, 0)
        if (group.some((e) => !isStreetWalk(e.leg))) {
          sections.push({
            kind: 'transfer',
            id: `transfer-${legIndex}`,
            sectionIndex: legIndex,
            title: strings.makeATransfer,
            totalTime: minutesLabel(nonStreetDuration),
          })
        }
        sections.push({
          kind: 'entry',
          id: `street-exit-${legIndex}`,
          entryType: 'exit',
          totalTime: '',
        })
        sections.push({
          kind: 'validation',
          id: `street-validation-exit-${legIndex}`,
          validationType: 'exit',
        })
        for (const { leg: walkLeg, index } of streetWalks) {
          sections.push(streetWalkSection(index, walkLeg))
          const map = walkMapSection(index, walkLeg, resolveStation)
          if (map !== null) sections.push(map)
        }
        sections.push({
          kind: 'validation',
          id: `street-validation-entry-${legIndex}`,
          validationType: 'entry',
        })
        sections.push({
          kind: 'entry',
          id: `street-entry-${legIndex}`,
          entryType: 'entry',
          totalTime: '',
        })
      }
    }
  })

  // 3) Entry (walk-out) then Validation (tap-out) — Figma pairs them at
  //    the bottom, validation LAST. A TRAILING street walk continues
  //    AFTER the tap-out: the rider leaves the arrival station, walks
  //    the street, and the list closes on the SELECTED destination:
  //    Entry(exit) → Tap out → StreetWalk → Marker(selected).
  sections.push({ kind: 'entry', id: 'exit', entryType: 'exit', totalTime: '' })
  sections.push({ kind: 'validation', id: 'validation-exit', validationType: 'exit' })
  if (trailingStreetWalks.length > 0) {
    for (const { leg, index } of trailingStreetWalks) {
      sections.push(streetWalkSection(index, leg))
    }
    const last = trailingStreetWalks[trailingStreetWalks.length - 1]
    sections.push(streetEndpointSection('destination', last.index, last.leg.to_station_id ?? ''))
  }

  // 4) Map of the destination — the trailing street walk's target when
  //    present (the station the user selected; the walk's route drawn on
  //    it), else the last stop of the last ride leg. Leading and mid-route
  //    street walks got their own walking maps above (walkMapSection).
  const lastTrailingWalk = trailingStreetWalks[trailingStreetWalks.length - 1]
  const lastRide = rides[rides.length - 1]
  const dest = lastRide?.leg.stops?.[lastRide.leg.stops.length - 1]
  if (lastTrailingWalk !== undefined) {
    // The journey ends on foot: the map draws that walk, from the last ride
    // landing to the destination, whatever its length (Android TASK-560
    // canon) — a single-pin map when the landing cannot be placed.
    const first = trailingStreetWalks[0]
    const id = lastTrailingWalk.leg.to_station_id ?? ''
    const originCoordinate = walkCoordinate(resolveStation, first.leg.from_station_id ?? '')
    sections.push({
      kind: 'map',
      id: 'map',
      stationName: stationName(resolveStation, id),
      ...(originCoordinate === null ? {} : { originCoordinate }),
      destinationCoordinate: walkCoordinate(resolveStation, id),
    })
  } else if (dest !== undefined) {
    sections.push({
      kind: 'map',
      id: 'map',
      stationName: stationName(resolveStation, dest.stationId),
      destinationCoordinate: walkCoordinate(resolveStation, dest.stationId),
    })
  }

  return sections
}

interface PushLineBlockArgs {
  sections: RouteDetailsSection[]
  stops: NonNullable<Leg['stops']>
  legIndex: number
  hexColor: string
  isOutlined: boolean
  subtitle: string | null
  totalTime: string
  platformBound: string | null
  extra: FirstStopExtra
  isExpanded: boolean
  resolveStation: StationResolver
  arrivalAt: (offsetS: number) => string
  strings: RouteDetailsStringSet
}

/**
 * Builds one multi-stop line block in Figma order. Mirrors the V1
 * fabric's `stops` assembly + expand/cut-off, then appends in order. The
 * "Towards …" line lives inside the first-stop row (Figma
 * `RD/Row-FirstStop`, under the platform chip), not in a row of its own;
 * the expand row shows only when the block has more than 3 stop entries,
 * and collapses to first/expand + last when not expanded.
 */
function pushLineBlock(args: PushLineBlockArgs): void {
  const {
    sections,
    stops,
    legIndex,
    hexColor,
    isOutlined,
    subtitle,
    totalTime,
    platformBound,
    extra,
    isExpanded,
    resolveStation,
    arrivalAt,
    strings,
  } = args

  const first = stops[0]
  const last = stops[stops.length - 1]
  const middles = stops.slice(1, -1)

  const firstStop: FirstStopSection = {
    kind: 'firstStop',
    id: `first-stop-${legIndex}`,
    sectionIndex: legIndex,
    title: stationName(resolveStation, first.stationId),
    subtitle,
    totalTime,
    arrivalTime: arrivalAt(first.arrival_offset_s),
    hexColor,
    isOutlined,
    isSingleStop: false,
    platformBound,
    // Towards = second stop's name (V1 `_getDirection` → stops[1]).
    towards: towardsLabel(stationName(resolveStation, stops[1].stationId), strings),
    extra,
  }

  const middleSections: MiddleStopSection[] = middles.map((stop, j) => ({
    kind: 'middleStop',
    id: `middle-stop-${legIndex}-${j}`,
    sectionIndex: legIndex,
    rowIndex: j,
    title: stationName(resolveStation, stop.stationId),
    arrivalTime: arrivalAt(stop.arrival_offset_s),
    hexColor,
    isOutlined,
  }))

  const lastStop: LastStopSection = {
    kind: 'lastStop',
    id: `last-stop-${legIndex}`,
    sectionIndex: legIndex,
    title: stationName(resolveStation, last.stationId),
    arrivalTime: arrivalAt(last.arrival_offset_s),
    hexColor,
    isOutlined,
  }

  // V1 counts: stop entries = firstStop + middles + lastStop. The expand
  // predicate is `total > 3`.
  const totalCount = 2 + middleSections.length // first + last + middles
  const showExpand = totalCount > 3

  sections.push(firstStop)

  if (showExpand) {
    const middleStopsCount = totalCount - 2 // = middles.length
    sections.push({
      kind: 'expand',
      id: `expand-${legIndex}`,
      sectionIndex: legIndex,
      title: stationsCountLabel(middleStopsCount, strings),
      isExpanded,
      hexColor,
      isOutlined,
    })
    if (isExpanded) {
      sections.push(...middleSections)
    }
    // collapsed → middles cut off (first/expand + last only).
  } else {
    sections.push(...middleSections)
  }

  sections.push(lastStop)
}

interface ResolveExtraArgs {
  route: Route
  legIndex: number
  isPremium: boolean
  cars: readonly ConvenientCarType[] | undefined
}

/**
 * Mirrors V1 `RouteDetailsOptionView` extra-slot gating + the fabric's
 * `possibleTransfers` predicate: the extra appears only when the line has
 * a transfer after it AND convenient cars exist. Premium → car-strip;
 * non-premium → upsell banner.
 */
function resolveExtra(args: ResolveExtraArgs): FirstStopExtra {
  const { route, legIndex, isPremium, cars } = args
  // The extra slot is gated on this ride having an interchange after it
  // (transfer + walk legs folded) AND convenient cars existing.
  if (foldedTransferAfter(route, legIndex) === null) return null
  if (cars === undefined || cars.length === 0) return null
  return isPremium ? { kind: 'possibleTransfer', cars } : { kind: 'upsell' }
}
